import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ShipmentFilters = ({ onFilterChange, onClearFilters }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [filters, setFilters] = useState({
    supplier: '',
    buyer: '',
    port: '',
    vesselType: '',
    status: [],
    dateFrom: '',
    dateTo: '',
    searchQuery: ''
  });

  const supplierOptions = [
    { value: '', label: 'All Suppliers' },
    { value: 'whitehaven', label: 'Whitehaven Coal' },
    { value: 'yancoal', label: 'Yancoal Australia' },
    { value: 'glencore', label: 'Glencore Coal Assets' },
    { value: 'peabody', label: 'Peabody Energy Australia' }
  ];

  const buyerOptions = [
    { value: '', label: 'All Buyers' },
    { value: 'adani', label: 'Adani Power Limited' },
    { value: 'ntpc', label: 'NTPC Limited' },
    { value: 'tata', label: 'Tata Power Company' },
    { value: 'jsw', label: 'JSW Energy Limited' }
  ];

  const portOptions = [
    { value: '', label: 'All Ports' },
    { value: 'newcastle', label: 'Port of Newcastle' },
    { value: 'gladstone', label: 'Port of Gladstone' },
    { value: 'hay-point', label: 'Hay Point' },
    { value: 'abbot-point', label: 'Abbot Point' }
  ];

  const vesselTypeOptions = [
    { value: '', label: 'All Vessel Types' },
    { value: 'panamax', label: 'Panamax (60-80k MT)' },
    { value: 'capesize', label: 'Capesize (150-180k MT)' },
    { value: 'supramax', label: 'Supramax (50-60k MT)' },
    { value: 'handymax', label: 'Handymax (40-50k MT)' }
  ];

  const statusOptions = [
    { value: 'contract-signed', label: 'Contract Signed' },
    { value: 'lc-received', label: 'LC Received' },
    { value: 'loading', label: 'Loading' },
    { value: 'in-transit', label: 'In Transit' },
    { value: 'discharged', label: 'Discharged' },
    { value: 'completed', label: 'Completed' }
  ];

  const handleFilterChange = (field, value) => {
    const updatedFilters = { ...filters, [field]: value };
    setFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleStatusToggle = (statusValue) => {
    const currentStatuses = filters?.status || [];
    const updatedStatuses = currentStatuses?.includes(statusValue)
      ? currentStatuses?.filter(s => s !== statusValue)
      : [...currentStatuses, statusValue];
    
    handleFilterChange('status', updatedStatuses);
  };

  const handleClear = () => {
    const clearedFilters = {
      supplier: '',
      buyer: '',
      port: '',
      vesselType: '',
      status: [],
      dateFrom: '',
      dateTo: '',
      searchQuery: ''
    };
    setFilters(clearedFilters);
    onClearFilters();
  };

  const activeFilterCount = Object.values(filters)?.filter(v => 
    Array.isArray(v) ? v?.length > 0 : v !== ''
  )?.length;

  return (
    <div className="bg-card border-r border-border h-full flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon name="Filter" size={20} color="var(--color-primary)" />
            <h3 className="font-semibold text-foreground">Filters</h3>
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-accent/10 rounded transition-colors"
            aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
          >
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={18} />
          </button>
        </div>

        <Input
          type="search"
          placeholder="Search shipments..."
          value={filters?.searchQuery}
          onChange={(e) => handleFilterChange('searchQuery', e?.target?.value)}
          className="mb-0"
        />
      </div>
      {isExpanded && (
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Date Range
            </label>
            <div className="space-y-2">
              <Input
                type="date"
                label="From"
                value={filters?.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
              />
              <Input
                type="date"
                label="To"
                value={filters?.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
              />
            </div>
          </div>

          <div>
            <Select
              label="Supplier"
              options={supplierOptions}
              value={filters?.supplier}
              onChange={(value) => handleFilterChange('supplier', value)}
            />
          </div>

          <div>
            <Select
              label="Buyer"
              options={buyerOptions}
              value={filters?.buyer}
              onChange={(value) => handleFilterChange('buyer', value)}
            />
          </div>

          <div>
            <Select
              label="Port"
              options={portOptions}
              value={filters?.port}
              onChange={(value) => handleFilterChange('port', value)}
            />
          </div>

          <div>
            <Select
              label="Vessel Type"
              options={vesselTypeOptions}
              value={filters?.vesselType}
              onChange={(value) => handleFilterChange('vesselType', value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Shipment Status
            </label>
            <div className="space-y-2">
              {statusOptions?.map((status) => (
                <Checkbox
                  key={status?.value}
                  label={status?.label}
                  checked={filters?.status?.includes(status?.value)}
                  onChange={() => handleStatusToggle(status?.value)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="p-4 border-t border-border">
        <Button
          variant="outline"
          fullWidth
          iconName="X"
          iconPosition="left"
          onClick={handleClear}
          disabled={activeFilterCount === 0}
        >
          Clear All Filters
        </Button>
      </div>
    </div>
  );
};

export default ShipmentFilters;